import React, { useState, useEffect } from 'react';
import { Search, X, Check, Building2, AlertCircle } from 'lucide-react';
import firmService from '../../services/firmService';

const SuperAdminFirmManager = () => {
  const [firms, setFirms] = useState([]);
  const [filteredFirms, setFilteredFirms] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [editingFirm, setEditingFirm] = useState(null);
  const [savingFirmId, setSavingFirmId] = useState(null);

  useEffect(() => {
    loadFirms();
  }, []);

  useEffect(() => {
    // Filter firms based on search term
    if (searchTerm) {
      const filtered = firms.filter(firm =>
        firm.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (firm.twilio_phone_number && firm.twilio_phone_number.includes(searchTerm))
      );
      setFilteredFirms(filtered);
    } else {
      setFilteredFirms(firms);
    }
  }, [searchTerm, firms]);

  const loadFirms = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await firmService.getAllFirms();
      setFirms(data);
      setFilteredFirms(data);
    } catch (err) {
      console.error('Error loading firms:', err);
      setError(err.response?.data?.detail || 'Failed to load firms');
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (firm) => {
    setEditingFirm({
      id: firm.id,
      twilio_phone_number: firm.twilio_phone_number || '',
      is_active: firm.is_active,
      support_chat_enabled: firm.support_chat_enabled || false
    });
  };

  const handleCancel = () => {
    setEditingFirm(null);
  };

  const handleSave = async (firmId) => {
    try {
      setSavingFirmId(firmId);
      setError(null);

      const updateData = {
        twilio_phone_number: editingFirm.twilio_phone_number || null,
        is_active: editingFirm.is_active,
        support_chat_enabled: editingFirm.support_chat_enabled
      };

      const updatedFirm = await firmService.updateFirm(firmId, updateData);

      // Update the firm in the list
      setFirms(prevFirms =>
        prevFirms.map(f => f.id === firmId ? updatedFirm : f)
      );

      setEditingFirm(null);
    } catch (err) {
      console.error('Error updating firm:', err);
      setError(err.response?.data?.detail || 'Failed to update firm');
    } finally {
      setSavingFirmId(null);
    }
  };

  const handleInputChange = (field, value) => {
    setEditingFirm(prev => ({
      ...prev,
      [field]: value
    }));
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-yellow-500"></div>
        <span className="ml-3 text-gray-600">Loading firms...</span>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow">
      {/* Header */}
      <div className="p-6 border-b border-gray-200">
        <div className="flex items-center justify-between">
          <div className="flex items-center">
            <Building2 className="h-6 w-6 text-yellow-600 mr-3" />
            <div>
              <h2 className="text-xl font-semibold text-gray-900">Firm Management</h2>
              <p className="text-sm text-gray-500 mt-1">
                Manage Twilio phone numbers and firm activation status (Super Admin only)
              </p>
            </div>
          </div>
          <div className="text-sm text-gray-500">
            {filteredFirms.length} {filteredFirms.length === 1 ? 'firm' : 'firms'}
          </div>
        </div>

        {/* Search Bar */}
        <div className="mt-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
            <input
              type="text"
              placeholder="Search firms by name or phone number..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-yellow-500 focus:border-transparent"
            />
          </div>
        </div>

        {/* Error Alert */}
        {error && (
          <div className="mt-4 p-4 bg-red-50 border border-red-200 rounded-md flex items-start">
            <AlertCircle className="h-5 w-5 text-red-600 mr-2 flex-shrink-0 mt-0.5" />
            <div className="text-sm text-red-700">{error}</div>
          </div>
        )}
      </div>

      {/* Table */}
      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Firm Name
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Twilio Phone Number
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Status
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Support Chat
              </th>
              <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {filteredFirms.length === 0 ? (
              <tr>
                <td colSpan="5" className="px-6 py-8 text-center text-gray-500">
                  {searchTerm ? 'No firms match your search' : 'No firms found'}
                </td>
              </tr>
            ) : (
              filteredFirms.map((firm) => {
                const isEditing = editingFirm?.id === firm.id;
                const isSaving = savingFirmId === firm.id;

                return (
                  <tr key={firm.id} className={isEditing ? 'bg-gray-50' : 'hover:bg-gray-50'}>
                    {/* Firm Name */}
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <Building2 className="h-5 w-5 text-gray-400 mr-2" />
                        <div>
                          <div className="text-sm font-medium text-gray-900">{firm.name}</div>
                          {firm.email && (
                            <div className="text-xs text-gray-500">{firm.email}</div>
                          )}
                        </div>
                      </div>
                    </td>

                    {/* Twilio Phone Number */}
                    <td className="px-6 py-4 whitespace-nowrap">
                      {isEditing ? (
                        <input
                          type="text"
                          value={editingFirm.twilio_phone_number}
                          onChange={(e) => handleInputChange('twilio_phone_number', e.target.value)}
                          placeholder="+17178836767"
                          className="w-full px-3 py-1.5 text-sm border border-blue-300 rounded focus:ring-2 focus:ring-yellow-500 focus:border-transparent"
                        />
                      ) : (
                        <div className="text-sm text-gray-900">
                          {firm.twilio_phone_number || (
                            <span className="text-gray-400 italic">Not set</span>
                          )}
                        </div>
                      )}
                    </td>

                    {/* Status */}
                    <td className="px-6 py-4 whitespace-nowrap">
                      {isEditing ? (
                        <select
                          value={editingFirm.is_active ? 'active' : 'inactive'}
                          onChange={(e) => handleInputChange('is_active', e.target.value === 'active')}
                          className="px-3 py-1.5 text-sm border border-blue-300 rounded focus:ring-2 focus:ring-yellow-500 focus:border-transparent"
                        >
                          <option value="active">Active</option>
                          <option value="inactive">Inactive</option>
                        </select>
                      ) : (
                        <span
                          className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${
                            firm.is_active
                              ? 'bg-green-100 text-green-800'
                              : 'bg-red-100 text-red-800'
                          }`}
                        >
                          {firm.is_active ? 'Active' : 'Inactive'}
                        </span>
                      )}
                    </td>

                    {/* Support Chat */}
                    <td className="px-6 py-4 whitespace-nowrap">
                      {isEditing ? (
                        <label className="inline-flex items-center cursor-pointer">
                          <input
                            type="checkbox"
                            checked={editingFirm.support_chat_enabled}
                            onChange={(e) => handleInputChange('support_chat_enabled', e.target.checked)}
                            className="sr-only peer"
                          />
                          <div className="relative w-9 h-5 bg-gray-200 peer-focus:ring-2 peer-focus:ring-yellow-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:start-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:bg-yellow-500"></div>
                        </label>
                      ) : (
                        <span
                          className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${
                            firm.support_chat_enabled
                              ? 'bg-green-100 text-green-800'
                              : 'bg-gray-100 text-gray-500'
                          }`}
                        >
                          {firm.support_chat_enabled ? 'Enabled' : 'Disabled'}
                        </span>
                      )}
                    </td>

                    {/* Actions */}
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      {isEditing ? (
                        <div className="flex items-center justify-end space-x-2">
                          <button
                            onClick={() => handleSave(firm.id)}
                            disabled={isSaving}
                            className="inline-flex items-center px-3 py-1.5 bg-yellow-500 text-white rounded hover:bg-yellow-600 disabled:opacity-50 disabled:cursor-not-allowed"
                          >
                            {isSaving ? (
                              <>
                                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-1"></div>
                                Saving...
                              </>
                            ) : (
                              <>
                                <Check className="h-4 w-4 mr-1" />
                                Save
                              </>
                            )}
                          </button>
                          <button
                            onClick={handleCancel}
                            disabled={isSaving}
                            className="inline-flex items-center px-3 py-1.5 bg-gray-200 text-gray-700 rounded hover:bg-gray-300 disabled:opacity-50 disabled:cursor-not-allowed"
                          >
                            <X className="h-4 w-4 mr-1" />
                            Cancel
                          </button>
                        </div>
                      ) : (
                        <button
                          onClick={() => handleEdit(firm)}
                          className="text-yellow-600 hover:text-gray-900"
                        >
                          Edit
                        </button>
                      )}
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default SuperAdminFirmManager;
